var _ = require('underscore');

var TorsoView = require('backbone-torso/modules/View');
var appointmentBillTemplate = require('./appointmentBillTemplate.hbs');
var portalConfig = require('./../design/patient-portal/portal-nav/portalConfig');

var financialFeatureService = require('./../financial/financialFeatureService');

var EstimatedBillsAccordionWidget = require('./../appointment-estimated-bill/EstimatedBillsAccordionWidget');
var PayableBillsAccordionWidget = require('./PayableBillsAccordionWidget');

var DemographicsDataBehavior = require('./../demographics/DemographicsDataBehavior');
var SecureMessageDataBehavior = require('./../secure-message/SecureMessageDataBehavior');
var AppointmentTypeDataBehavior = require('./../appointment-type/AppointmentTypeDataBehavior');
var AppointmentTypeParentBehavior = require('./../behaviors/AppointmentTypeParentBehavior');
var WizardAccessBehavior = require('./../wizard/WizardAccessBehavior');
var WizardPerspectiveBehavior = require('./../wizard/WizardPerspectiveBehavior');
var wizardUtils = require('./../wizard/wizardUtils');

/**
* View to display the Appointment Bill page
*
* @class AppointmentBillView
* @param options {Object} Options to configure this perspective.
*   @param [options.patientVecnaId] {String} the vecna id of the current patient
*   @param [options.appointmentExternalId] {String} the external id of the current appointment
*/
module.exports = TorsoView.extend({
  tagName: 'section',
  className: 'brec-main',
  template: appointmentBillTemplate,
  parentNavItem: portalConfig.pageItems.appointments.name,

  behaviors: {
    demographics: {
      behavior: DemographicsDataBehavior,
      returnSingleResult: true,
      renderOnFetch: true,
      id: { property: '_patientVecnaId' }
    },
    appointmentTypeParentBehavior: {
      behavior: AppointmentTypeParentBehavior,
      renderOnParentFetched: false
    },
    appointments: {
      behavior: SecureMessageDataBehavior,
      alwaysFetch: true,
      ids: function(cache) {
        var appointmentExternalIds = this.view._appointmentExternalIds;
        if (appointmentExternalIds) {
          var criteria = {
            isPatient: true,
            appointmentCriteria: {
              externalAppointmentCriteria: {
                externalIds: appointmentExternalIds
              }
            }
          };
          return cache.fetchIdsByCriteria(criteria);
        }
        return null;
      }
    },
    appointmentType: {
      behavior: AppointmentTypeDataBehavior,
      renderOnFetch: true,
      updateEvents: 'behaviors.appointments.data.privateCollection:add',
      ids: { property: 'behaviors.appointments.data:appointmentDTO.externalAppointment.type'}
    },
    parentAppointmentType: {
      behavior: AppointmentTypeDataBehavior,
      ids: {property: 'behaviors.appointmentType.data:parentExternalId'}
    },
    grandparentAppointmentType: {
      behavior: AppointmentTypeDataBehavior,
      ids: {property: 'behaviors.parentAppointmentType.data:parentExternalId'}
    },
    wizardAccessBehavior: WizardAccessBehavior,
    wizardPerspectiveBehavior: {
      behavior: WizardPerspectiveBehavior,
      pageName: WizardPerspectiveBehavior.PAGE_NAMES.PAYMENT,
      childPageName: WizardPerspectiveBehavior.PAGE_NAMES.BILL_DETAIL
    }
  },

  prepareFields: {
    totalAmount: '_getTotalAmount',
    showEstimates: function() {
      return this._checkShowEstimateStatus();
    },
    showPayableBills: function() {
      return this._checkShowPayableBillsStatus();
    },
    showEstimateAndPayableBills: function() {
      return this._checkShowEstimateStatus() && this._checkShowPayableBillsStatus();
    },
    showEstimateOrPayableBills: function() {
      return this._checkShowEstimateStatus() || this._checkShowPayableBillsStatus();
    },
    abilityToPayInfo: function() {
      return financialFeatureService.getAbleToPayState(this._patientVecnaId);
    },
    allWithoutAccountNumber: function() {
      return this._payableBillsAccordionWidget && this._payableBillsAccordionWidget.allBillsWithoutAccountNumber();
    },
    someBillWithoutAccountNumber: function() {
      return this._payableBillsAccordionWidget && this._payableBillsAccordionWidget.hasBillsWithoutAccountNumber();
    }
  },

  injectionSites: {
    'estimated-bill-list': '_estimatedBillsAccordionWidget',
    'unpaid-bill-list': '_payableBillsAccordionWidget'
  },

  /**
   * @method initialize
   * @override
   * @param options {Object} Options to configure this perspective.
   *   @param [options.patientVecnaId] {String} the vecna id of the current patient
   *   @param [options.appointmentExternalId] {String} the external id of the current appointment
   *   @param [options.regDate] {String} the date of the current appointments
   */
  initialize: function(options) {
    // These are set for the wizard behaviors.
    this._appointmentExternalId = options.appointmentExternalId;
    this._patientVecnaId = options.patientVecnaId;
    this._regDate = options.regDate;
    this._appointmentExternalIds =
        wizardUtils.getSelectedPreRegWizardAppointmentExternalIds(this._appointmentExternalId, this._regDate);

    this.set('hide:unpaid-bill-list', true);
    this.set('hide:estimated-bill-list', true);

    // only initialize estimated bill and payable bills widgets if the configs for them on the appointmentType are on
    this.listenTo(this.getBehavior('appointmentType'), 'change:parent', this._updateOrCreateEstimatedBillsAccordionWidget);
    this.listenTo(this.getBehavior('appointmentType'), 'change:parent', this._updateOrCreatePayableBillsAccordionWidget);
    this.listenTo(this.getBehavior('parentAppointmentType'), 'change:parent', this._updateOrCreateEstimatedBillsAccordionWidget);
    this.listenTo(this.getBehavior('parentAppointmentType'), 'change:parent', this._updateOrCreatePayableBillsAccordionWidget);

    this.listenTo(this.viewState, 'change:hide:unpaid-bill-list', this._changePaymentAllowed);
    this.listenTo(this.viewState, 'change:hide:estimated-bill-list', this._changePaymentAllowed);
  },

  /**
   * Initializes payable bills widget if configs for payable bills are turned on
   * @method _updateOrCreateEstimatedBillsAccordionWidget
   * @private
   */
  _updateOrCreateEstimatedBillsAccordionWidget: function() {
    var appointmentTypes = this.getBehavior('appointmentType').data.getModels();
    var allowEstimatedPayTypes = _.filter(appointmentTypes, function(appointmentType) {
      return appointmentType.getEffectiveAllowEstimatedPay();
    });
    var allowEstimatedPayTypeNames = _.map(allowEstimatedPayTypes, function(appointmentType) {
      return appointmentType.get('externalId');
    });

    if (!_.isEmpty(allowEstimatedPayTypes)) {
      var appointments = this.getBehavior('appointments').data.getModels();
      var filteredAppointments = _.filter(appointments, function (appointment) {
        return _.contains(allowEstimatedPayTypeNames, appointment.get('appointmentDTO.externalAppointment.type'));
      });
      var appointmentExternalIds = _.map(filteredAppointments, function (appointment) {
        return appointment.get('appointmentDTO.externalId');
      });
      if (!this._estimatedBillsAccordionWidget) {
        this._estimatedBillsAccordionWidget = new EstimatedBillsAccordionWidget({
          appointmentExternalIds: appointmentExternalIds,
          asListItem: true
        });
      }
      this.set('hide:estimated-bill-list', false);
    }
    this._attachEstimatedBillsAccordionWidgetListeners();
  },

  /**
   * Attach listeners for the payable bills widget and estimated bills widget if they exist
   * @method _attachEstimatedBillsAccordionWidgetListeners
   * @private
   */
  _attachEstimatedBillsAccordionWidgetListeners: function() {
    if (this._estimatedBillsAccordionWidget) {
      this.listenTo(this._estimatedBillsAccordionWidget, 'change:totalEstimatedBillsAmount', this._changePaymentAllowed);
      this.listenTo(this._estimatedBillsAccordionWidget, 'change:totalEstimatedBillsAmount', this.render);
      this.listenTo(this.viewState, 'change:hide:estimated-bill-list', this._changePaymentAllowed);
      this.listenTo(this.viewState, 'change:hide:estimated-bill-list', this.render);
    }
  },

  /**
   * Detach listeners for the payable bills widget and estimated bills widget if they exist
   * @method _detachEstimatedBillsAccordionWidgetListeners
   * @private
   */
  _detachEstimatedBillsAccordionWidgetListeners: function() {
    if (this._estimatedBillsAccordionWidget) {
      this.stopListening(this._estimatedBillsAccordionWidget, 'change:totalEstimatedBillsAmount', this._changePaymentAllowed);
      this.stopListening(this._estimatedBillsAccordionWidget, 'change:totalEstimatedBillsAmount', this.render);
      this.stopListening(this.viewState, 'change:hide:estimated-bill-list', this._changePaymentAllowed);
      this.stopListening(this.viewState, 'change:hide:estimated-bill-list', this.render);
    }
  },

  /**
   * Initializes payable bills widget if configs for payable bills are turned on
   * @method _updateOrCreatePayableBillsAccordionWidget
   * @private
   */
  _updateOrCreatePayableBillsAccordionWidget: function() {
    var appointmentTypes = this.getBehavior('appointmentType').data.getModels();
    var allowOutstandingPay = _.reduce(appointmentTypes, function(allowOutstandingPay, appointmentType){
      return allowOutstandingPay || appointmentType.getEffectiveAllowOutstandingPay();
    }, false);

    if (!this._payableBillsAccordionWidget && allowOutstandingPay) {
      this._payableBillsAccordionWidget = new PayableBillsAccordionWidget();
    }
    if (allowOutstandingPay) {
      this.set('hide:unpaid-bill-list', false);
    }
    this._attachPayableBillsAccordionWidgetListeners();
  },

  /**
   * Attach listeners for the payable bills widget if it exists
   * @method _attachPayableBillsAccordionWidgetListeners
   * @private
   */
  _attachPayableBillsAccordionWidgetListeners: function() {
    if (this._payableBillsAccordionWidget) {
      this.listenTo(this._payableBillsAccordionWidget, 'change:totalUnpaidBillsAmount', this._changePaymentAllowed);
      this.listenTo(this._payableBillsAccordionWidget, 'change:totalUnpaidBillsAmount', this.render);
      this.listenTo(this.viewState, 'change:hide:unpaid-bill-list', this._changePaymentAllowed);
      this.listenTo(this.viewState, 'change:hide:unpaid-bill-list', this.render);
    }
  },

  /**
   * Detach listeners for the payable bills widget if it exists
   * @method _attachPayableBillsAccordionWidgetListeners
   * @private
   */
  _detachPayableBillsAccordionWidgetListeners: function() {
    if (this._payableBillsAccordionWidget) {
      this.stopListening(this._payableBillsAccordionWidget, 'change:totalUnpaidBillsAmount', this._changePaymentAllowed);
      this.stopListening(this._payableBillsAccordionWidget, 'change:totalUnpaidBillsAmount', this.render);
      this.stopListening(this.viewState, 'change:hide:unpaid-bill-list', this._changePaymentAllowed);
      this.stopListening(this.viewState, 'change:hide:unpaid-bill-list', this.render);
    }
  },

  /**
   * @method _attached
   * @private
   * @override
   */
  _attached: function() {
    this._attachEstimatedBillsAccordionWidgetListeners();
    this._attachPayableBillsAccordionWidgetListeners();
    financialFeatureService.fetchPayAbility(this._patientVecnaId)
      .then(this.render);
  },

  /**
   * @method _detached
   * @private
   * @override
   */
  _detached: function() {
    this._detachEstimatedBillsAccordionWidgetListeners();
    this._detachPayableBillsAccordionWidgetListeners();
  },

  /**
   * @method _changePaymentAllowed
   * @private
   */
  _changePaymentAllowed: function() {
    var showPayableBills = this._checkShowPayableBillsStatus();
    var allWithoutAccountNumber = this._payableBillsAccordionWidget && this._payableBillsAccordionWidget.allBillsWithoutAccountNumber();
    var showEstimates = this._checkShowEstimateStatus();
    var allWithUnapprovedUpdates = this._estimatedBillsAccordionWidget && this._estimatedBillsAccordionWidget.allEstimatedBillsWithUnapprovedUpdates();
    var paymentAllowed = (showPayableBills && !allWithoutAccountNumber) || (showEstimates && !allWithUnapprovedUpdates);
    paymentAllowed = paymentAllowed && financialFeatureService.getAbleToPayState(this._patientVecnaId).ableToPay;

    this.getBehavior('wizardPerspectiveBehavior').getWizardWidget().setPaymentAllowed(paymentAllowed);
  },

  /**
   * @method _getTotalAmount
   * @private
   */
  _getTotalAmount: function() {
    var showEstimates = this._checkShowEstimateStatus();
    var allWithUnapprovedUpdates = this._estimatedBillsAccordionWidget && this._estimatedBillsAccordionWidget.allEstimatedBillsWithUnapprovedUpdates();
    var totalEstimatedAmount = !this._estimatedBillsAccordionWidget || allWithUnapprovedUpdates ? 0 : this._estimatedBillsAccordionWidget.getTotalEstimatedBillsAmount();
    totalEstimatedAmount = showEstimates ? totalEstimatedAmount : 0;

    var showPayableBills = this._checkShowPayableBillsStatus();
    var totalUnpaidAmount = this._payableBillsAccordionWidget && this._payableBillsAccordionWidget.getTotalUnpaidBillsAmount();
    totalUnpaidAmount = showPayableBills ? totalUnpaidAmount : 0;

    return totalEstimatedAmount + totalUnpaidAmount;
  },

  /**
   * @method _checkShowEstimateStatus
   * @private
   */
  _checkShowEstimateStatus: function() {
    return this._estimatedBillsAccordionWidget && this._estimatedBillsAccordionWidget.hasEstimates() && !this.get('hide:estimated-bill-list');
  },

  /**
   * @method _checkShowPayableBillsStatus
   * @private
   */
  _checkShowPayableBillsStatus: function() {
    return this._payableBillsAccordionWidget && this._payableBillsAccordionWidget.hasPayableBills() && !this.get('hide:unpaid-bill-list');
  }
});
